import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  TextField,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle,
  Link as LinkIcon,
  Settings as SettingsIcon,
  Done as DoneIcon,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ga4ContextService, GA4Property } from '../../services/ga4ContextService';
import { useGA4Store } from '../../store/ga4.store';
import { useSiteStore } from '../../store/site.store';

const steps = ['Connect Google Account', 'Select GA4 Property', 'Configure Metrics', 'Complete'];

export const GA4SetupWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setGA4Status } = useGA4Store();
  const { selectedSite } = useSiteStore();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const oauthProcessedRef = useRef(false); // Prevent duplicate OAuth processing
  
  // Step 1: Property selection (auto-fetched)
  const [availableProperties, setAvailableProperties] = useState<GA4Property[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  
  // Step 2: Engagement Metrics
  const [engagementMetrics, setEngagementMetrics] = useState([
    { name: 'engaged_sessions', label: 'Engaged Sessions', description: 'Sessions with meaningful engagement', selected: true },
    { name: 'session_duration', label: 'Session Duration', description: 'Time users spend on pages', selected: true },
    { name: 'pages_per_session', label: 'Pages per Session', description: 'Content depth indicator', selected: true },
    { name: 'bounce_rate', label: 'Bounce Rate', description: 'Single-page sessions', selected: true },
    { name: 'scroll_depth', label: 'Scroll Depth', description: 'Content consumption depth', selected: false },
    { name: 'event_value', label: 'Event Value', description: 'Business value from engagement', selected: false },
  ]);
  
  // Step 3: Page Mapping
  const [stripQueryStrings, setStripQueryStrings] = useState(true);

  // Check if we're returning from OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    
    if (code && state && !oauthProcessedRef.current) {
      // Mark as processed immediately to prevent duplicate calls
      oauthProcessedRef.current = true;
      
      console.log('OAuth callback received - exchanging code for tokens');
      
      // Exchange OAuth code for tokens via backend
      const exchangeOAuthCode = async () => {
        try {
          setLoading(true);
          
          console.log('========================================');
          console.log('📤 CALLING /oauth/callback');
          console.log('========================================');
          
          const result = await ga4ContextService.handleGA4OAuthCallback(code, state);
          
          console.log('========================================');
          console.log('✅ BACKEND RESPONSE FROM /oauth/callback:');
          console.log(JSON.stringify(result, null, 2));
          console.log('========================================');
          
          // Clear URL parameters to prevent re-processing
          setSearchParams({});
          
          // Now fetch available GA4 properties
          const siteId = selectedSite?.id;
          if (siteId) {
            await fetchGA4Properties(siteId);
          }
          
          // Advance to step 1 (property selection)
          setActiveStep(1);
          setLoading(false);
        } catch (err: any) {
          console.error('========================================');
          console.error('❌ OAUTH CALLBACK FAILED:');
          console.error('Error:', err);
          console.error('Response data:', err.response?.data);
          console.error('Status:', err.response?.status);
          console.error('========================================');
          
          setError('OAuth authentication failed. Please try again.');
          setLoading(false);
          
          // Clear URL parameters even on error
          setSearchParams({});
        }
      };
      
      exchangeOAuthCode();
    }
  }, [searchParams, setSearchParams, selectedSite]);

  // Fetch GA4 properties from backend
  const fetchGA4Properties = async (siteId: number) => {
    try {
      setPropertiesLoading(true);
      const properties = await ga4ContextService.listGA4Properties(siteId);
      setAvailableProperties(properties);
      if (properties.length === 1) {
        // Auto-select if only one property
        setSelectedPropertyId(properties[0].propertyId);
      }
      setPropertiesLoading(false);
    } catch (err: any) {
      console.error('Failed to fetch GA4 properties:', err);
      setError('Failed to fetch GA4 properties. Please try again.');
      setPropertiesLoading(false);
    }
  };
  
  const handleNext = async () => {
    setError(null);
    
    if (activeStep === 0) {
      // Step 0: Initiate OAuth (no property ID needed yet)
      try {
        setLoading(true);
        
        // Get the current site ID
        const siteId = selectedSite?.id;
        if (!siteId) {
          throw new Error('No site selected. Please select a site first.');
        }
        
        const response = await ga4ContextService.initiateGA4OAuth(siteId);
        
        if (!response || !response.authorizationUrl) {
          throw new Error('No authorization URL returned from server');
        }
        
        console.log('Redirecting to:', response.authorizationUrl);
        window.location.href = response.authorizationUrl;
      } catch (err: any) {
        console.error('OAuth initiation error:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to initiate OAuth';
        setError(`Failed to start GA4 connection: ${errorMessage}`);
        setLoading(false);
      }
    } else if (activeStep === 1) {
      // Step 1: Select GA4 property
      if (!selectedPropertyId) {
        setError('Please select a GA4 property');
        return;
      }
      setActiveStep((prev) => prev + 1);
    } else if (activeStep === 2) {
      // Step 2: Save engagement metrics and complete setup
      console.log('Step 2: selectedPropertyId =', selectedPropertyId);
      console.log('Strip query strings:', stripQueryStrings);
      
      if (!selectedPropertyId) {
        setError('Property ID is missing. Please restart the setup process.');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Save GA4 configuration to backend
        const selectedMetrics = engagementMetrics.filter(m => m.selected).map(m => m.name);
        
        console.log('========================================');
        console.log('📤 SENDING /link REQUEST:');
        console.log('========================================');
        console.log(JSON.stringify({
          tenantId: 1,
          propertyId: selectedPropertyId,
          engagementMetrics: selectedMetrics,
          stripQueryStrings: stripQueryStrings
        }, null, 2));
        console.log('========================================');
        
        await ga4ContextService.linkGA4Property(
          selectedPropertyId,
          1, // TODO: Get real tenant ID
          selectedMetrics,
          stripQueryStrings
        );
        
        console.log('✅ /link request successful');
        
        // Update local store
        setGA4Status({
          connected: true,
          propertyId: selectedPropertyId,
          conversionEventsCount: engagementMetrics.filter(m => m.selected).length,
          lastSyncedAt: new Date().toISOString(),
        });
        
        // Trigger initial sync
        console.log('========================================');
        console.log('📤 SENDING /sync REQUEST:');
        console.log('========================================');
        
        await ga4ContextService.triggerGA4Sync(1); // TODO: Get real tenant ID
        
        console.log('✅ /sync request successful');
        console.log('🎉 GA4 CONFIGURATION COMPLETE!');
        
        setLoading(false);
        setActiveStep((prev) => prev + 1);
      } catch (err: any) {
        console.error('Failed to complete GA4 setup:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to save configuration';
        setError(`Failed to complete setup: ${errorMessage}`);
        setLoading(false);
      }
    } else if (activeStep === 3) {
      // Step 3: Complete - navigate back to configuration
      navigate('/webtools/config');
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate('/webtools/config'); // Return to Site Configuration
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleToggleMetric = (metricName: string) => {
    setEngagementMetrics((prev) =>
      prev.map((metric) =>
        metric.name === metricName ? { ...metric, selected: !metric.selected } : metric
      )
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" alignItems="center" gap={2}>
                  <LinkIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6">Connect Your Google Account</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Authorize access to your Google Analytics 4 properties
                    </Typography>
                  </Box>
                </Box>

                <Alert severity="info">
                  <Typography variant="body2">
                    You'll be redirected to Google to authorize access. After authorization, 
                    you'll select which GA4 property to connect from your available properties.
                  </Typography>
                </Alert>

                <Paper elevation={0} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    What we'll access:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="List your GA4 properties"
                        secondary="To let you choose which property to connect"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Engagement metrics"
                        secondary="Session duration, bounce rate, pages per session"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="User behavior data"
                        secondary="Sessions, users, scroll depth, interaction events"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Read-only access"
                        secondary="We won't modify your GA4 data"
                      />
                    </ListItem>
                  </List>
                </Paper>

                {error && (
                  <Alert severity="error">
                    <Typography variant="body2">{error}</Typography>
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" alignItems="center" gap={2}>
                  <CheckCircle color="success" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6">Select GA4 Property</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose which GA4 property to connect to this site
                    </Typography>
                  </Box>
                </Box>

                <Alert severity="success">
                  <Typography variant="body2">
                    ✅ Google account connected successfully!
                  </Typography>
                </Alert>

                {propertiesLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : availableProperties.length === 0 ? (
                  <Alert severity="warning">
                    <Typography variant="body2">
                      No GA4 properties found for your Google account. Please ensure you have 
                      access to at least one GA4 property and try again.
                    </Typography>
                  </Alert>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel id="ga4-property-label">GA4 Property</InputLabel>
                    <Select
                      labelId="ga4-property-label"
                      value={selectedPropertyId}
                      label="GA4 Property"
                      onChange={(e) => setSelectedPropertyId(e.target.value)}
                    >
                      {availableProperties.map((prop) => (
                        <MenuItem key={prop.propertyId} value={prop.propertyId}>
                          {prop.displayName} ({prop.propertyId})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {error && (
                  <Alert severity="error">
                    <Typography variant="body2">{error}</Typography>
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" gap={3}>
                <Box display="flex" alignItems="center" gap={2}>
                  <SettingsIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6">Configure Engagement Metrics</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select metrics to enrich SEO insights with user behavior
                    </Typography>
                  </Box>
                </Box>

                <Alert severity="success">
                  <Typography variant="body2">
                    ✅ Selected GA4 property: {availableProperties.find(p => p.propertyId === selectedPropertyId)?.displayName || selectedPropertyId}
                  </Typography>
                </Alert>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Engagement Metrics:
                  </Typography>
                  <List>
                    {engagementMetrics.map((metric) => (
                      <ListItem key={metric.name} disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={metric.selected}
                              onChange={() => handleToggleMetric(metric.name)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{metric.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {metric.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={stripQueryStrings}
                      onChange={(e) => setStripQueryStrings(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">Strip Query Strings</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Normalize URLs by removing query parameters for better matching
                      </Typography>
                    </Box>
                  }
                />

                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Recommended:</strong> Enable core engagement metrics (sessions, duration, bounce rate) to identify which pages drive meaningful user interaction.
                  </Typography>
                </Alert>

                {error && (
                  <Alert severity="error">
                    <Typography variant="body2">{error}</Typography>
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" gap={3} py={4}>
                <DoneIcon color="success" sx={{ fontSize: 80 }} />
                
                <Box textAlign="center">
                  <Typography variant="h5" gutterBottom>
                    Setup Complete!
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Your GA4 property is now connected
                  </Typography>
                </Box>

                <Paper elevation={0} sx={{ p: 3, backgroundColor: 'success.lighter', width: '100%' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    What's next:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="✅ GA4 Property Connected"
                        secondary={availableProperties.find(p => p.propertyId === selectedPropertyId)?.displayName || selectedPropertyId}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="✅ Initial data sync started"
                        secondary="Data will be available in 5-10 minutes"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="✅ Engagement metrics configured"
                        secondary={`Tracking ${engagementMetrics.filter(e => e.selected).length} metrics`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="✅ URL normalization configured"
                        secondary={stripQueryStrings ? 'Query strings stripped' : 'Full URLs matched'}
                      />
                    </ListItem>
                  </List>
                </Paper>

                {loading && (
                  <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Syncing data...
                    </Typography>
                  </Box>
                )}

                <Alert severity="info" sx={{ width: '100%' }}>
                  <Typography variant="body2">
                    <strong>GSC remains source of truth.</strong> GA4 provides business value
                    context to help you prioritize optimization efforts.
                  </Typography>
                </Alert>
              </Box>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box display="flex" flexDirection="column" gap={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton 
            onClick={() => navigate('/webtools/config')}
            aria-label="back to site configuration"
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 0 }}>
              GA4 Context Layer Setup
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Connect Google Analytics 4 to unlock business value insights
            </Typography>
          </Box>
        </Box>

        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent()}

        <Box display="flex" justifyContent="space-between">
          <Button
            onClick={handleBack}
            variant="outlined"
            disabled={loading}
          >
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 1 ? (
              'Finish'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
